import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Skeleton,
  Pagination,
} from "@mui/material";
import AddTestimonialModal from "./components/AddTestimonials";
import {
  TESTIMONIALS_PAGE_SIZE,
  useTestimonials,
} from "./hooks/UseTestimonials";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const TestimonialsManager = () => {
  const { hasPermission } = usePermissions();
  const {
    testimonials,
    totalCount,
    loading,
    error,
    fetchTestimonials,
    saveTestimonial,
  } = useTestimonials();

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const pageCount = Math.ceil(totalCount / TESTIMONIALS_PAGE_SIZE);

  useEffect(() => {
    fetchTestimonials(page);
  }, [fetchTestimonials, page]);

  const handleAddTestimonial = async (data: {
    author_name: string;
    author_description?: string;
    content: string;
    rating?: number;
  }) => {
    await saveTestimonial({
      author_name: data.author_name,
      author_description: data.author_description,
      content: data.content,
      rating: data.rating ?? 5,
      is_active: true,
    });
    fetchTestimonials(page);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 4,
        }}
      >
        {hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            Add Testimonial
          </Button>
        )}
      </Box>

      {/* Error State */}
      {error && (
        <Typography color="error" align="center" sx={{ mb: 3 }}>
          {error}
        </Typography>
      )}

      {/* Loading State */}
      {loading && (
        <Grid container spacing={4}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper sx={{ p: 4, borderRadius: 4 }}>
                <Skeleton variant="text" height={80} />
                <Skeleton
                  variant="text"
                  height={30}
                  sx={{ width: "60%", mx: "auto" }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Testimonials Grid */}
      {!loading && testimonials.length > 0 && (
        <Grid container spacing={4}>
          {testimonials.map((testimonial) => (
            <Grid key={testimonial.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  textAlign: "center",
                  height: "100%",
                  boxShadow: 0,
                }}
              >
                <Typography sx={{ lineHeight: 1.7, mb: 3 }}>
                  {testimonial.content}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Brittany, cursive",
                    fontSize: 24,
                  }}
                >
                  {testimonial.author_name}
                </Typography>

                {testimonial.author_description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {testimonial.author_description}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalCount > TESTIMONIALS_PAGE_SIZE && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            disabled={loading}
            color="primary"
          />
        </Box>
      )}

      {/* Empty State */}
      {!loading && testimonials.length === 0 && !error && (
        <Typography align="center" color="text.secondary">
          No testimonials yet.
        </Typography>
      )}

      {/* Modal */}
      <AddTestimonialModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleAddTestimonial}
      />
    </Box>
  );
};

export default TestimonialsManager;
